import { UseFormReturn } from 'react-hook-form';
import {
    IDictFilters,
    IDocumentTable,
    IFormValue,
    IFormValues,
    ISetValue,
    ITableColumnDict,
    TableColumn,
} from '@models/Forms/IForms';
import { IDictFilter } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import { ValueType } from '@srm/ui/types/ValueType';
import { FormulaManager, hasRemoteFormulaData } from '@utils/managers/FormulaManager';
import { SetConditionManager } from '@utils/managers/SetConditionManager';
import {
    ChangesFieldValue,
    GetFormulaValues,
    GetValueForSetValue,
    type InvokeSavedChange,
} from '@utils/managers/ChangesManager';
import { getFieldKeysByChanges } from '@utils/documentUtils';
import { parseRuDate } from '@utils/date';
import { toLocalDate } from '@srm/ui/lib/toLocalDate';
import { getTableColumnFormulaTraceTarget, publishFormulaTrace } from '@utils/documentFormulaTraceUtils';
import type { DocumentFormulaTraceRegistry, IFormulaTraceTarget } from '@/context/DocumentFormulaTraceContext';
import { setValueTyped } from '@utils/helpers';
import { DictExternalDataSource } from '@utils/DictExternalDataSource';
import { prepareCodeValue, prepareFormDataRowValue, resolveSetValue } from './editTableValueUtils';
import { loadMathJs } from '@utils/mathjsLoader';
import { TableRow } from './tableColumns';
import { isDocumentFieldKey, type ChangeRuntime, type IChangeInvocation } from '@utils/managers/ChangeRuntime';

export interface ITableFormBridgeDeps<TFieldValues extends object = object> {
    fields: Record<string, IFieldElem>;
    formMethods: UseFormReturn<TFieldValues>;
    isEdit: boolean;
    isNew: boolean;
    setError: (messages?: string[]) => void;
    /** Родительская строка (режим подтаблицы master-detail): |Parent|-поля
     *  формул единообразно резолвятся во всех методах моста. */
    rowParent?: TableRow;
    /** Реестр трасс формул (инспектор DebugPanel): без него публикация — no-op. */
    traceRegistry?: DocumentFormulaTraceRegistry;
    changeRuntime?: ChangeRuntime;
    invokeSavedChange?: InvokeSavedChange;
    scopeId?: string;
    isCurrent?: () => boolean;
}

/** Пустой выбор в пикере: чистится и сама колонка, и всё, что она проставляет. */
function clearedSelectionPatch(column: { key: string; setValues?: { sets?: ISetValue[] } }): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    patch[column.key] = '';
    column.setValues?.sets?.forEach((el) => {
        patch[el.key] = '';
    });
    return patch;
}

/**
 * Мост «таблица форм ↔ RHF/движки формул» (EditTable).
 * Семантический паритет с одноимёнными функциями EditTable (см.
 * docs/tanstack-table-migration/research/formulas-changes-pipeline.md), но без
 * DevExtreme: строки живут только в форме, запись — точечный setValue.
 * Дублирование со старой веткой временное — она умирает вместе с DX.
 */
export function createTableFormBridge<TFieldValues extends object = object>({
    fields,
    formMethods,
    isEdit,
    isNew,
    setError,
    rowParent: defaultRowParent,
    traceRegistry,
    changeRuntime,
    invokeSavedChange: dispatchSavedChange,
    scopeId,
    isCurrent,
}: ITableFormBridgeDeps<TFieldValues>) {
    const invokeSavedChange: InvokeSavedChange | undefined = (dispatchSavedChange || changeRuntime)
        ? (target, value, row, activeTargets, oldRow) => {
            const binding = { ...target, scopeId: isDocumentFieldKey(target.key) ? undefined : target.scopeId ?? scopeId };
            return dispatchSavedChange
                ? dispatchSavedChange(binding, value, row, activeTargets, oldRow)
                : changeRuntime!.invoke(binding, value, { activeTargets: activeTargets ?? new Set(), scopeId }, row, oldRow);
        }
        : undefined;
    const pendingRowChanges = new WeakMap<object, Array<{ key: string; tableKey: string; scopeId?: string; row: TableRow; oldRow?: TableRow; activeTargets?: Set<string> }>>();
    const queueSavedRowChange = (row: TableRow, key: string, tableKey: string, targetRow = row,
        oldRow?: TableRow, activeTargets?: Set<string>, targetScopeId?: string) => {
        const pending = pendingRowChanges.get(row) ?? [];
        pending.push({ key, tableKey, row: targetRow, oldRow, activeTargets, scopeId: targetScopeId });
        pendingRowChanges.set(row, pending);
    };
    /** Значение поля формы (документа или формы строки) по имени. */
    const getFormFieldValue = (fieldName: string): unknown => {
        const field = fields[fieldName];
        if (!field) return undefined;
        return formMethods.getValues(field.name as never);
    };
    /** Значение поля по имени: документное, родительской строки, текущей строки
     *  или формы строки. */
    const getRowFieldValue = (fieldName: string, rowData?: TableRow, rowParent = defaultRowParent): unknown => {
        if (fieldName.includes('|Document') || fieldName.startsWith('#links')) return getFormFieldValue(fieldName);
        if (fieldName.startsWith('|Parent|') && rowParent && rowParent !== defaultRowParent)
            return rowParent[fieldName.replace('|Parent', '')];
        if (fieldName.startsWith('|Parent|') && fields[fieldName]) return getFormFieldValue(fieldName);
        if (fieldName.includes('|Parent') && rowParent) return rowParent[fieldName.replace('|Parent', '')];
        if (rowData && fieldName in rowData) return rowData[fieldName];
        // Подтаблица внутри формы строки (edit-in-form): правила её колонок смотрят на
        // поля родительской строки («|LinkDocument|Дата_поставки_план» в графике
        // поставки), а те живут не в строке подтаблицы, а в полях формы строки.
        return getFormFieldValue(fieldName);
    };
    /** Запись значения set-правила в строку (даты нормализуются) или в поле документа. */
    const onSaveFieldWithOutUpdate = async (
        item: ISetValue,
        table?: IDocumentTable,
        rowData?: TableRow,
        value?: unknown,
    ) => {
        // ноль — значение: подставлять запасное надо только когда значения нет
        let val = await resolveSetValue(value, () =>
            GetValueForSetValue(item, table?.key, fields, rowData, formMethods),
        );
        if (isCurrent?.() === false) return { saved: false, value: undefined };
        if (isDocumentFieldKey(item.key)) {
            const field = fields[item.key];
            if (!field) return { saved: false, value: undefined };
            formMethods.setValue(field.name as never, val as never, { shouldDirty: true });
            return { saved: true, value: formMethods.getValues(field.name as never) };
        }
        if (!rowData) return { saved: false, value: undefined };
        const column = table?.tableColumn?.find((col) => col.key === item.key);
        const isDateColumn =
            column &&
            (column.valueType === ValueType.Date ||
                column.valueType === ValueType.DateTime ||
                column.valueType === ValueType.NoSecDateTime);
        if (isDateColumn) {
            if (typeof val === 'string' || typeof val === 'number') {
                const parsed = (typeof val === 'string' && parseRuDate(val)) || toLocalDate(val);
                // непарсибельная строка ('' от очищенного источника, прочерк) не
                // должна превращаться в Invalid Date в ячейке — значение чистим
                val = !isNaN(parsed.getTime()) ? parsed : null;
            }
            rowData[item.key] = val instanceof Date ? val : null;
        } else {
            rowData[item.key] = val;
        }
        return { saved: true, value: rowData[item.key] };
    };

    /** onSaveField старой ветки, но точечный setValue строки формы (паритет EUPDEV-12056). */
    const onSaveField = async (
        item: ISetValue,
        table?: IDocumentTable,
        rowData?: TableRow,
        value?: unknown,
        withoutUpdateForm?: boolean,
    ) => {
        const saved = await onSaveFieldWithOutUpdate(item, table, rowData, value);
        if (!saved?.saved || !table || withoutUpdateForm || isDocumentFieldKey(item.key)) return saved;
        const tablePath = scopeId ?? fields[table.key]?.name;
        if (!tablePath || !rowData) return { saved: false, value: undefined };
        const tableRows = formMethods.getValues(tablePath as never);
        const rowsArray = Array.isArray(tableRows) ? (tableRows as TableRow[]) : [];
        const idx = rowsArray.findIndex((o) => rowData['|NUM'] === o['|NUM']);
        if (idx >= 0) {
            formMethods.setValue(`${tablePath}.${idx}` as never, rowData as never, { shouldDirty: false });
            return { saved: true, value: (formMethods.getValues(`${tablePath}.${idx}` as never) as TableRow)?.[item.key] };
        }
        return { saved: false, value: undefined };
    };

    /** Булева/числовая формула правила таблицы/колонки в контексте строки.
     *  `trace` — цель в инспекторе формул: с ним вычисление и отказ публикуются
     *  в реестр (диагностика «почему ячейка залочена» на живом стенде). */
    const evalTableFormulaValue = async (
        condition: string,
        rowData?: TableRow,
        rowParent = defaultRowParent,
        trace?: IFormulaTraceTarget,
    ) => {
        const manager = new FormulaManager(condition);
        // Рекурсивная валидация/видимость передаёт своего непосредственного
        // родителя, который важнее алиасов внешней формы строки.
        const formulaFields =
            rowParent && rowParent !== defaultRowParent
                ? Object.fromEntries(Object.entries(fields).filter(([key]) => !key.startsWith('|Parent|')))
                : fields;
        manager.Init(formulaFields, formMethods, rowParent);
        const coll = manager.GetFields();
        let values: unknown[] | undefined = coll.map((f) => getRowFieldValue(f, rowData, rowParent));
        if (values.every((element) => element === undefined)) values = undefined;
        try {
            const result = await manager.EvalFormulaValues(isEdit, isNew, values as never);
            publishFormulaTrace({
                registry: traceRegistry,
                target: trace,
                source: condition,
                manager,
                fields,
                watchedValues: values,
                result,
                status: 'evaluated',
            });
            return result;
        } catch (error: unknown) {
            publishFormulaTrace({
                registry: traceRegistry,
                target: trace,
                source: condition,
                manager,
                fields,
                watchedValues: values,
                result: undefined,
                status: 'error',
                error: error instanceof Error ? error.message : String(error),
            });
            throw error;
        }
    };

    /** Формулы, результат которых нельзя переносить с строки на строку: обращения
     *  к серверу и справочникам, факты таблицы, генераторы и «текущий момент». */
    const VOLATILE_FORMULA =
        /\b(FACT|DICTEXIST|GETDATAFROMSP|CHECKROW|SETGUID|NOW|NOWONLYDATE|CURRENTYEAR|GETLINKSCOUNT|GETATTACHMENTSCOUNT)\s*\(/i;
    /** Разбор формулы дорог (три десятка сканирований регулярками), а правила
     *  колонок разбираются на каждую строку — держим разобранное по формуле. */
    const ruleManagers = new Map<string, FormulaManager>();
    /** Результаты правил по «формула + её значения». Держим ПРОМИС, а не значение:
     *  строки считаются пачкой параллельно, и одинаковые правила успевают уйти в
     *  вычисление до того, как первое из них завершится. Ограничение — от
     *  разрастания на таблицах, где значения правил у каждой строки свои. */
    const ruleResults = new Map<string, Promise<unknown>>();
    const RULE_RESULTS_LIMIT = 20000;

    const isScalarValue = (value: unknown) =>
        value == null ||
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        value instanceof Date;
    const scalarKey = (value: unknown) =>
        value instanceof Date ? `d${value.getTime()}` : `${typeof value}:${String(value)}`;

    /**
     * Правило колонки или кнопки по строке — с переиспользованием результата.
     *
     * Правила читают у всех строк одни и те же поля («{|Document|Переторжка}!=1»,
     * «{|Тип}==2»), и на таблице в тысячи строк различных наборов значений —
     * единицы. Формула при этом остаётся чистой функцией от значений своих полей,
     * поэтому ключ кэша — сама формула плюс эти значения; поля документа в набор
     * входят наравне со строкой, так что их правка ключ меняет.
     *
     * Из кэша выключены формулы, которые смотрят наружу (сервер, справочник,
     * факты, время), нескалярные значения (значение таблицы целиком) и работа при
     * открытом инспекторе формул — трасса обязана показывать подстановки именно
     * этой строки.
     */
    const evalTableRuleValue = async (
        condition: string,
        rowData?: TableRow,
        rowParent = defaultRowParent,
        trace?: IFormulaTraceTarget,
    ): Promise<unknown> => {
        if (
            traceRegistry ||
            rowParent !== defaultRowParent ||
            VOLATILE_FORMULA.test(condition) ||
            hasRemoteFormulaData(condition)
        ) {
            return evalTableFormulaValue(condition, rowData, rowParent, trace);
        }
        let manager = ruleManagers.get(condition);
        if (!manager) {
            manager = new FormulaManager(condition);
            manager.Init(fields, formMethods, rowParent);
            ruleManagers.set(condition, manager);
        }
        const values = manager.GetFields().map((f) => getRowFieldValue(f, rowData, rowParent));
        if (!values.every(isScalarValue)) return evalTableFormulaValue(condition, rowData, rowParent, trace);
        // JSON-массив, а не склейка через разделитель: значение со случайным
        // разделителем внутри не должно совпасть с другим набором значений
        const key = `${condition}::${JSON.stringify(values.map(scalarKey))}`;
        const cached = ruleResults.get(key);
        if (cached) return cached;
        const pending = manager
            .EvalFormulaValues(
                isEdit,
                isNew,
                (values.every((value) => value === undefined) ? undefined : values) as never,
            )
            .catch((error: unknown) => {
                // сбой не кэшируем: следующая строка должна получить свой шанс
                ruleResults.delete(key);
                throw error;
            });
        if (ruleResults.size >= RULE_RESULTS_LIMIT) ruleResults.clear();
        ruleResults.set(key, pending);
        return pending;
    };

    /** Пересчёт вычисляемых колонок строки (tableColumnCalc); мутирует и возвращает row. */
    const calculateRow = async (row: TableRow, column: TableColumn | undefined, table: IDocumentTable) => {
        if (!table.tableColumnCalc) return row;
        // Ключи, затронутые правкой колонки, от item не зависят — считаем один раз.
        let keysChanged: string[] = [];
        if (column) {
            keysChanged.push(column.key);
            if ('changes' in column && column.changes) {
                keysChanged = keysChanged.concat(getFieldKeysByChanges(column.changes, false, Array.from(changeRuntime?.actions.values() ?? [])));
            }
        }
        for (const item of table.tableColumnCalc) {
            const affected =
                column === undefined ||
                (column.key !== item.key && keysChanged.some((x) => item.formula.indexOf(x) > -1));
            if (!affected) continue;

            const formulaMng = new FormulaManager(item.formula);
            formulaMng.Init(fields, formMethods, defaultRowParent);
            const coll = formulaMng.GetFields();
            let values: unknown[] | undefined = undefined;
            if (row && coll.length > 0) {
                values = GetFormulaValues(coll, table.key, fields, row, formMethods);
            }
            const traceTarget = traceRegistry
                ? getTableColumnFormulaTraceTarget(table, item as TableColumn, 'formula', row)
                : undefined;
            try {
                const val = await formulaMng.EvalFormulaValues(isEdit, isNew, values as never);
                // текстовая колонка хранит результат как есть — как Calc и calculateRowFormTree
                if (item.valueType === ValueType.Text) {
                    row[item.key] = val;
                } else {
                    let valForm = isNaN(val) ? 0 : val;
                    if (item.floatPoints) {
                        const { round } = await loadMathJs();
                        valForm = round(valForm, item.floatPoints);
                    }
                    row[item.key] = +valForm;
                }
                publishFormulaTrace({
                    registry: traceRegistry,
                    target: traceTarget,
                    source: item.formula,
                    manager: formulaMng,
                    fields,
                    watchedValues: values,
                    result: row[item.key],
                    status: 'evaluated',
                });
            } catch (error: unknown) {
                publishFormulaTrace({
                    registry: traceRegistry,
                    target: traceTarget,
                    source: item.formula,
                    manager: formulaMng,
                    fields,
                    watchedValues: values,
                    result: undefined,
                    status: 'error',
                    error: error instanceof Error ? error.message : String(error),
                });
                throw error;
            }
        }
        return row;
    };

    /** Изменения (changes) правленной колонки; пишет в row и/или поля документа. */
    const onChangeCellValue = async (
        row: TableRow,
        oldRow: TableRow,
        column: TableColumn,
        table: IDocumentTable,
        withOutUpdate?: boolean,
        invocation?: IChangeInvocation,
    ) => {
        if (!column?.key || !table?.key || !('changes' in column)) return;
        await ChangesFieldValue(
            column.changes,
            row[column.key],
            table.key,
            fields,
            isEdit,
            isNew,
            withOutUpdate ? onSaveFieldWithOutUpdate : onSaveField,
            setError,
            table,
            row,
            formMethods,
            oldRow,
            withOutUpdate && invokeSavedChange
                ? async (target, _value, targetRow, activeTargets, targetOldRow) => {
                    queueSavedRowChange(row, target.key, target.tableKey ?? table.key,
                        targetRow ?? row, targetOldRow ?? oldRow, activeTargets, target.scopeId);
                    return true;
                }
                : invokeSavedChange,
            invocation?.activeTargets ?? new Set(),
            changeRuntime?.actions,
        );
    };

    /** Применить набор setValues.sets к строке (newRowData/copyRowData и т.п.). */
    const applyRowSets = async (sets: ISetValue[] | undefined, table: IDocumentTable, row: TableRow): Promise<void> => {
        if (!sets?.length) return;
        for (const item of sets) {
            const saved = await onSaveField(item, table, row, undefined, true);
            if (item.invokeChanges && saved?.saved) {
                queueSavedRowChange(row, item.key, table.key);
            }
        }
    };

    /** Заполнение новой строки из записи справочника (addFormDataRows.setValues):
     *  attr='code' → код записи, иначе — одноимённое поле записи с типизацией
     *  setValueTyped. Паритет onSetFormDataNewRow старой ветки; setCondition
     *  проверяется по подставляемому значению, как в обычном справочнике. */
    const applyFormDataRow = async (
        sets: ISetValue[] | undefined,
        table: IDocumentTable,
        row: TableRow,
        data: IDictionaryData,
    ): Promise<void> => {
        if (!sets?.length) return;
        for (const set of sets) {
            // Числовой ноль из справочника — значение, а не «пусто» (EUPDEV-12655).
            const prepared = set.attr === 'code'
                ? prepareCodeValue(data.code)
                : prepareFormDataRowValue(data.fields.find((field) => field.name === set.attr)?.value, set.type,
                    (value) => setValueTyped(value, set));
            if (!prepared.shouldSave) continue;
            if (
                set.setCondition &&
                !(await new SetConditionManager(prepared.value, fields, formMethods, isNew, isEdit).checkConditionAsync(
                    set.setCondition,
                ))
            )
                continue;
            const saved = await onSaveField(set, table, row, prepared.value, true);
            if (set.invokeChanges && saved?.saved) {
                queueSavedRowChange(row, set.key, table.key);
            }
        }
    };

    /** Invoke setValues cascades only after EditTable has published the new row array. */
    const flushPendingRowChanges = async (row: TableRow): Promise<void> => {
        const pending = pendingRowChanges.get(row) ?? [];
        pendingRowChanges.delete(row);
        for (const target of pending) {
            const documentTarget = isDocumentFieldKey(target.key);
            const tablePath = target.scopeId ?? scopeId ?? fields[target.tableKey]?.name;
            const rows = tablePath ? formMethods.getValues(tablePath as never) : undefined;
            // RHF клонирует публикуемые строки; предыдущий каскад мог заменить или удалить строку.
            const currentRow = Array.isArray(rows)
                ? (rows as TableRow[]).find((candidate) => candidate === target.row ||
                    (target.row['|NUM'] != null && candidate['|NUM'] === target.row['|NUM']))
                : undefined;
            if (!documentTarget && !currentRow) continue;
            await invokeSavedChange?.(
                { key: target.key, ...(documentTarget ? {} : { tableKey: target.tableKey, scopeId: tablePath, rowId: currentRow!['|NUM']?.toString() }) },
                documentTarget ? getFormFieldValue(target.key) : currentRow![target.key],
                documentTarget ? undefined : currentRow,
                target.activeTargets ?? new Set(),
                documentTarget ? undefined : target.oldRow,
            );
        }
    };

    /** Каскад изменений жизненного цикла строки (onDeletedRows/onCopiedRows/…):
     *  changes таблицы + подтаблиц с инвалидацией фактов. */
    const runRowLifecycleChanges = async (
        pick: (t: IDocumentTable) => IDocumentTable['changes'] | undefined,
        value: unknown,
        row: TableRow | undefined,
        table: IDocumentTable,
        includeSubtables = true,
    ) => {
        FormulaManager.InvalidateFacts(table.key);
        await ChangesFieldValue(
            pick(table)!,
            value,
            table.key,
            fields,
            isEdit,
            isNew,
            onSaveField,
            setError,
            table,
            row,
            formMethods,
            undefined, invokeSavedChange, new Set(), changeRuntime?.actions,
        );
        for (const subTable of includeSubtables ? table.tables ?? [] : []) {
            await ChangesFieldValue(
                pick(subTable)!,
                value,
                subTable.key,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                subTable,
                row,
                formMethods,
                undefined, invokeSavedChange, new Set(), changeRuntime?.actions,
            );
        }
    };

    const onTableRowDeleted = (value: unknown, row: TableRow, table: IDocumentTable) =>
        runRowLifecycleChanges((t) => t.onDeletedRows, value, row, table);
    const onTableRowCopied = (value: unknown, row: TableRow, table: IDocumentTable) =>
        runRowLifecycleChanges((t) => t.onCopiedRows, value, row, table);
    const onFormRowEdited = (value: unknown, row: TableRow, table: IDocumentTable, includeSubtables = true) =>
        runRowLifecycleChanges((t) => t.onFormRowEdited, value, row, table, includeSubtables);

    /** Копия полей документа для формы строки (TableForm). */
    const getParentFields = () => {
        const values = formMethods.getValues() as { fields?: Array<Record<string, unknown>> };
        return (values.fields ?? []).map((field) => ({ ...field })) as never[];
    };

    /** Запись поля документа из формы строки. */
    const setParentField = (field: { name: string; value: unknown }) => {
        const target = fields[field.name];
        if (target) formMethods.setValue(target.name as never, field.value as never, { shouldDirty: true });
    };

    /** Булево правило по данным строки (видимость построчных кнопок).
     *  Полный резолв: правила смешивают поля строки с полями документа
     *  («{|Document|Переторжка}!=1 && {|По_критерию}!='1'»), а резолв только
     *  по строке отдавал документную часть в undefined — правило падало
     *  и кнопка оставалась видимой. */
    const evalRowRule = async (rules: string | undefined, rowData: TableRow): Promise<boolean> => {
        if (!rules) return true;
        return !!(await evalTableRuleValue(rules, rowData));
    };

    /** watch-поля формул (для useWatch-реактивности правил) — без дубликатов.
     *  Дедупликация обязательна: один и тот же путь (например, текущая таблица —
     *  зависимость каждой колоночной visibility-формулы) повторялся по разу на
     *  формулу, и watch-key стрингифаил значение таблицы столько же раз —
     *  на 1500 строках это сотни МБ и секунды на каждый проход. Потребители
     *  используют результат только как список имён для useWatch, порядок
     *  первых вхождений Set сохраняет. */
    const getWatchesByFormula = (formulas: Array<string | undefined>, rowParent = defaultRowParent): string[] => {
        const result = new Set<string>();
        for (const formula of formulas) {
            if (!formula) continue;
            const manager = new FormulaManager(formula);
            manager.Init(fields, formMethods, rowParent);
            manager.GetWatchFields().forEach((item: string) => result.add(item));
        }
        return Array.from(result);
    };

    /** Каскад таблицы (агрегаты в поля документа) + changes подтаблиц. */
    const onTableChange = async (value: unknown, table: IDocumentTable, includeSubtables = true, invocation?: IChangeInvocation) => {
        // Содержимого таблицы-линков в форме нет: оно едет отдельным запросом (pending) либо запрос
        // отказал (loadFailed). Каскад по заглушке [] посчитал бы агрегаты и set-правила по пустой
        // таблице и записал бы нули в поля документа. Гейт здесь, а не только у вызывающих: правило
        // принадлежит каскаду, и любая новая точка входа получает его сразу. Правкам пользователя
        // это не мешает — в таком состоянии таблица не редактируется.
        const dataField = fields[table.key];
        if (dataField?.pending || dataField?.loadFailed) return;
        FormulaManager.InvalidateFacts(table.key);
        await ChangesFieldValue(
            table.changes!,
            value,
            table.key,
            fields,
            isEdit,
            isNew,
            async (item: ISetValue, _table?: IDocumentTable, rowData?: TableRow, val?: unknown) => {
                return await onSaveField(item, undefined, rowData, val);
            },
            setError,
            table,
            undefined,
            formMethods,
            undefined, invokeSavedChange, invocation?.activeTargets ?? new Set(), changeRuntime?.actions,
        );
        for (const subTable of includeSubtables ? (table.tables ?? []) : []) {
            await ChangesFieldValue(
                subTable.changes!,
                value,
                subTable.key,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                subTable,
                undefined,
                formMethods,
                undefined, invokeSavedChange, invocation?.activeTargets ?? new Set(), changeRuntime?.actions,
            );
        }
    };

    /** Значение поля документа для параметра formValue: по column — выборка из массива,
     *  при `{join}` — она же строкой. */
    const getDocumentFormValue = (ele: IFormValue): unknown => {
        const field = fields[ele.attr];
        if (!field) return undefined;
        const val = formMethods.getValues(`fields.[${field.index}].value` as never) as unknown;
        if (!ele.column) return val;
        const arr = val as Array<Record<string, unknown>> | undefined;
        return ele.function === '{join}'
            ? (arr?.map((item) => `'${item[ele.column]}'`).join(',') ?? val)
            : (arr?.map((item) => item[ele.column]) ?? val);
    };

    /** formValues справочника → JSON-строка параметров (паритет EditTable.getFormValuesAsync). */
    const getFormValuesAsync = async (formValues: IFormValues | undefined, row: TableRow): Promise<string> => {
        const map = {} as Record<string, unknown>;
        for (const ele of formValues?.formValue ?? []) {
            if (ele.attr?.includes('|Document')) {
                if (ele.formula) {
                    const manager = new FormulaManager(ele.formula);
                    manager.Init(fields, formMethods);
                    map[ele.key] = await manager.ReplaceFormulaValues(isEdit, isNew);
                } else {
                    map[ele.key] = getDocumentFormValue(ele);
                }
            } else if (ele.formula) {
                const manager = new FormulaManager(ele.formula);
                manager.Init(fields, formMethods);
                const values = manager.GetFields().map((f) => getRowFieldValue(f, row));
                map[ele.key] = await manager.ReplaceFormulaValues(isEdit, isNew, values as never);
            } else {
                map[ele.key] = getRowFieldValue(ele.attr, row);
            }
        }
        return JSON.stringify(map);
    };

    /** Фильтры справочника с формульными условиями (паритет EditTable.getFiltersAsync). */
    const getFiltersAsync = async (dictFilters: IDictFilters | undefined, row: TableRow): Promise<IDictFilter> => {
        const result = {} as IDictFilter;
        for (const ele of dictFilters?.filter ?? []) {
            const conditionMng = new FormulaManager(ele.condition);
            conditionMng.Init(fields, formMethods);
            const values = conditionMng.GetFields().map((f) => getRowFieldValue(f, row));
            if (await conditionMng.EvalFormulaValues(isEdit, isNew, values as never)) {
                result.condition = ele.condition;
                if (ele.filter) {
                    const filterMng = new FormulaManager(ele.filter);
                    filterMng.Init(fields, formMethods);
                    result.filter = (await filterMng.ReplaceFormulaValues(isEdit, isNew))!;
                }
                if (ele.script) {
                    const scriptMng = new FormulaManager(ele.script);
                    scriptMng.Init(fields, formMethods);
                    result.script = (await scriptMng.ReplaceFormulaValues(isEdit, isNew))!;
                }
            }
        }
        return result;
    };

    /** Значения справочника не с сервера, а из самой карточки (externalDataSource:
     *  поля документа или строки другой таблицы) — контракт пикера синхронный. */
    const getDictExternalData = (column: ITableColumnDict): IDictionaryData[] =>
        new DictExternalDataSource(column.externalDataSource).GetData(formMethods, fields);

    /** Синхронный вариант formValues → JSON (контракт Abookpicker). */
    const getFormValues = (formValues: IFormValues | undefined, row: TableRow): string => {
        const map = {} as Record<string, unknown>;
        for (const ele of formValues?.formValue ?? []) {
            if (ele.attr.includes('|Document')) {
                map[ele.key] = getDocumentFormValue(ele);
            } else {
                map[ele.key] = row[ele.attr];
            }
        }
        return JSON.stringify(map);
    };

    /** Патч строки по выбору в адресной книге: ключи через '|' + setValues.sets
     *  (атрибуты записи АК под setCondition) — паритет onChange Abookpicker-редактора. */
    const abookSelectionPatch = async (
        column: { key: string; setValues?: { sets?: ISetValue[] } },
        row: TableRow,
        items: Array<Record<string, unknown>>,
    ): Promise<Record<string, unknown>> => {
        const joined = (attr: string) => items.map((item) => item[attr]?.toString()).join('|');
        if (items.length === 0) return clearedSelectionPatch(column);
        const patch: Record<string, unknown> = {};
        patch[column.key] = joined('key');
        for (const el of column.setValues?.sets ?? []) {
            if (el.attr === 'empty') {
                patch[el.key] = row[el.key] === '' ? undefined : '';
                continue;
            }
            const val = joined(el.attr);
            const conditionManager = new SetConditionManager(val, fields, formMethods, isNew, isEdit);
            if (await conditionManager.checkConditionAsync(el.setCondition)) {
                patch[el.key] = val;
            }
        }
        return patch;
    };

    /** Патч строки по выбору в справочнике: значение колонки (codes через '|') +
     *  setValues.sets (attr/формула/empty под setCondition) — паритет onChange
     *  старого Dictpicker-редактора. */
    const dictSelectionPatch = async (
        column: ITableColumnDict,
        row: TableRow,
        items: IDictionaryData[],
    ): Promise<Record<string, unknown>> => {
        const joined = (attr: string) =>
            items
                .map((item) =>
                    attr === 'code' ? item.code : item.fields.find((f) => f.name === attr)?.value?.toString(),
                )
                .join('|');
        if (items.length === 0) return clearedSelectionPatch(column);
        const patch: Record<string, unknown> = {};
        patch[column.key] = joined('code');
        for (const el of column.setValues?.sets ?? []) {
            let val: unknown;
            if (el.attr === 'empty') {
                val = row[el.key] === '' ? undefined : '';
            } else if (el.attr && el.attr.includes('{')) {
                val = await GetValueForSetValue(el, undefined, fields, { ...row, ...patch }, formMethods);
            } else {
                val = setValueTyped(joined(el.attr), el);
            }
            const conditionManager = new SetConditionManager(val, fields, formMethods, isNew, isEdit);
            if (await conditionManager.checkConditionAsync(el.setCondition)) {
                patch[el.key] = val;
            }
        }
        return patch;
    };

    return {
        /** Публиковать ли трассы: без реестра потребители не строят target'ы зря. */
        traceEnabled: !!traceRegistry,
        onSaveField,
        onSaveFieldWithOutUpdate,
        evalTableFormulaValue,
        evalTableRuleValue,
        calculateRow,
        onChangeCellValue,
        onTableChange,
        getRowFieldValue,
        getFormValuesAsync,
        getFormValues,
        getFiltersAsync,
        dictSelectionPatch,
        abookSelectionPatch,
        getDictExternalData,
        applyRowSets,
        applyFormDataRow,
        flushPendingRowChanges,
        onTableRowDeleted,
        onTableRowCopied,
        onFormRowEdited,
        getParentFields,
        setParentField,
        getWatchesByFormula,
        evalRowRule,
    };
}

export type TableFormBridge = ReturnType<typeof createTableFormBridge>;
